/**
 * OfficerListPage
 *
 * Public officer directory (accessible to all authenticated roles). Lists
 * officers from the existing `GET /officers` endpoint via the officerSlice
 * `fetchOfficers` thunk (no new API). Search + cadre filter + a details
 * dialog so the page is self-contained — does not depend on the profile route.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, FormControl, InputLabel, Select, IconButton, Tooltip, Chip, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { People as PeopleIcon, Visibility as ViewIcon, Badge as BadgeIcon } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { PageScaffold, StatusBadge } from '../../components/common';
import { fetchOfficers } from '../../store/slices/officerSlice';
import { RootState, AppDispatch } from '../../store';

interface OfficerRow {
  id: number;
  userId?: number;
  username?: string;
  email?: string;
  mobile?: string;
  fullName?: string;
  employeeId?: string;
  cadre?: string;
  cadreDisplayName?: string;
  designation?: string;
  department?: string;
  postingLocation?: string;
  batchYear?: number;
  joiningDate?: string;
  residenceCity?: string;
  residenceState?: string;
  residencePincode?: string;
  fullResidenceAddress?: string;
  status?: string;
  statusDisplayName?: string;
  verificationStatus?: boolean;
  familyMembersCount?: number;
  createdAt?: string;
}

const OfficerListPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const officersState = useSelector((s: RootState) => s.officer);
  const [search, setSearch] = useState('');
  const [cadreFilter, setCadreFilter] = useState('');
  const [view, setView] = useState<OfficerRow | null>(null);

  useEffect(() => { dispatch(fetchOfficers() as any); }, [dispatch]);

  const officers: OfficerRow[] = useMemo(() => {
    const raw = officersState.officers as unknown;
    return Array.isArray(raw) ? (raw as OfficerRow[]) : ((raw as any)?.content as OfficerRow[]) || [];
  }, [officersState.officers]);

  const cadres = useMemo(() => {
    const set = new Set<string>();
    officers.forEach((o) => { if (o.cadreDisplayName) set.add(o.cadreDisplayName); });
    return Array.from(set).sort();
  }, [officers]);

  const filtered = officers.filter((o) => {
    if (cadreFilter && o.cadreDisplayName !== cadreFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        o.fullName?.toLowerCase().includes(q) ||
        o.employeeId?.toLowerCase().includes(q) ||
        o.department?.toLowerCase().includes(q) ||
        o.postingLocation?.toLowerCase().includes(q) ||
        o.email?.toLowerCase().includes(q) ||
        o.mobile?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const columns: GridColDef[] = [
    { field: 'fullName', headerName: 'Officer', flex: 1.4, minWidth: 200, renderCell: (p) => p.row.fullName || '—' },
    { field: 'cadreDisplayName', headerName: 'Cadre', width: 200, renderCell: (p) => p.row.cadreDisplayName || p.row.cadre || '—' },
    { field: 'designation', headerName: 'Designation', width: 180, renderCell: (p) => p.row.designation || '—' },
    { field: 'department', headerName: 'Department', flex: 1, minWidth: 160, renderCell: (p) => p.row.department || '—' },
    { field: 'postingLocation', headerName: 'Posting', width: 140, renderCell: (p) => p.row.postingLocation || '—' },
    { field: 'mobile', headerName: 'Mobile', width: 130, renderCell: (p) => p.row.mobile || '—' },
    {
      field: 'verificationStatus', headerName: 'Verified', width: 110, sortable: false,
      renderCell: (p) => (p.row.verificationStatus
        ? <Chip size="small" color="success" label="Verified" />
        : <Chip size="small" color="warning" label="Pending" />),
    },
    {
      field: 'status', headerName: 'Status', width: 120,
      renderCell: (p) => <StatusBadge status={p.row.statusDisplayName || p.row.status || '—'} size="small" />,
    },
    {
      field: 'actions', headerName: 'View', width: 80, sortable: false,
      renderCell: (p) => (
        <Tooltip title="View details">
          <IconButton size="small" onClick={() => setView(p.row as OfficerRow)}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const DetailRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={{ minWidth: 150, color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
    </Stack>
  );

  return (
    <PageScaffold
      title="Officers"
      subtitle={`${officers.length} officer${officers.length === 1 ? '' : 's'} registered`}
      icon={<PeopleIcon />}
      loading={officersState.loading && officers.length === 0}
      error={officersState.error}
      onDismissError={() => {}}
    >
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search name / id / dept / posting / mobile" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 300 }} />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Cadre</InputLabel>
          <Select label="Cadre" value={cadreFilter} onChange={(e) => setCadreFilter(e.target.value)}>
            <MenuItem value="">All Cadres</MenuItem>
            {cadres.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ height: 560, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid
          rows={filtered}
          columns={columns}
          getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={view != null} onClose={() => setView(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BadgeIcon /> {view?.fullName || 'Officer'}
        </DialogTitle>
        <DialogContent dividers>
          {view && (
            <Box>
              <DetailRow label="Employee ID" value={view.employeeId} />
              <DetailRow label="Cadre" value={view.cadreDisplayName || view.cadre} />
              <DetailRow label="Designation" value={view.designation} />
              <DetailRow label="Department" value={view.department} />
              <DetailRow label="Posting Location" value={view.postingLocation} />
              <DetailRow label="Batch Year" value={view.batchYear} />
              <DetailRow label="Joining Date" value={view.joiningDate} />
              <DetailRow label="Email" value={view.email} />
              <DetailRow label="Mobile" value={view.mobile} />
              <DetailRow label="Residence" value={[view.fullResidenceAddress, view.residenceCity, view.residenceState, view.residencePincode].filter(Boolean).join(', ')} />
              <DetailRow label="Family Members" value={view.familyMembersCount} />
              <DetailRow label="Verification" value={view.verificationStatus ? 'Verified' : 'Pending'} />
              <DetailRow label="Status" value={<StatusBadge status={view.statusDisplayName || view.status || '—'} size="small" />} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setView(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageScaffold>
  );
};

export default OfficerListPage;